import * as util from './util';

/**
 * permission/isAuth 응답 판정.
 *
 * 왜 message 를 안 보는가 (2026-08-13 측정):
 * 예전에는 인증 성공 시 `message: "Success"` 였고 화면은 그것만 봤다. 그런데 서버가
 * 바뀌면서 **유효한 토큰에도 `message: "Failed"`** 가 내려온다. 응답 본문에는
 * 로그인한 사용자의 프로필 40개 키가 그대로 들어 있는데도 그렇다.
 * 그 결과 `message != "Success"` 로 판정하던 화면은 정상 로그인 상태에서도
 * 매 화면 이동마다 강제 로그아웃됐다.
 *
 *   유효한 토큰  → 키 40개 (id / user_id / name / email …), message="Failed"
 *   잘못된 토큰  → 키  1개 (프로필 없음),                    message="Failed"
 *
 * 즉 message 로는 두 경우를 구분할 수 없고, **프로필의 존재와 신원**으로는 구분된다.
 * 그래서 신원을 기준으로 판정한다. 서버가 message 를 다시 "Success" 로 고쳐도
 * 이 판정은 그대로 통과하므로 따로 되돌릴 필요가 없다.
 *
 * 원래 이 검사의 목적은 "다른 곳에서 같은 계정에 로그인하면 이쪽을 로그아웃"이었다.
 * 신원 비교는 그 목적을 문자열 검사보다 정확히 수행한다 — 응답의 사용자가
 * 이 기기에 저장된 사용자와 다르면 세션이 바뀐 것이다.
 */
export type AuthVerdict =
  /** 정상 — 저장된 계정과 같은 사용자로 인증돼 있다 */
  | 'authenticated'
  /** 인증되지 않음 (토큰 없음·만료·위조) */
  | 'rejected'
  /** 다른 계정으로 세션이 바뀌었다 */
  | 'other-account';

/** isAuth 응답에서 로그인 아이디(예: '12344575')를 꺼낸다 */
const loginIdOf = (data: any): string => util.strNull(data?.user_id);

/** isAuth 응답에서 고객 번호(예: '20000009')를 꺼낸다 */
const customerIdOf = (data: any): string =>
  util.strNull(data?.id) || util.strNull(data?.userId);

export function readAuthVerdict(
  data: any,
  storedLoginId: string,
  storedCustomerId: string,
): AuthVerdict {
  if (data === null || data === undefined || typeof data !== 'object') {
    return 'rejected';
  }

  const loginId = loginIdOf(data);
  const customerId = customerIdOf(data);

  // 프로필이 하나도 없으면 인증 거부다 (거부 응답은 message 한 개만 온다)
  if (loginId === '' && customerId === '') {
    return 'rejected';
  }

  // 저장된 값이 있을 때만 비교한다 — 한쪽이 비어 있으면 판단 근거가 없다
  if (storedLoginId !== '' && loginId !== '' && storedLoginId !== loginId) {
    return 'other-account';
  }
  if (storedCustomerId !== '' && customerId !== '' && storedCustomerId !== customerId) {
    return 'other-account';
  }

  return 'authenticated';
}

/** 화면에서 쓰는 축약형 — 로그아웃시켜야 하면 true */
export function shouldForceLogout(
  data: any,
  storedLoginId: string,
  storedCustomerId: string,
): boolean {
  return readAuthVerdict(data, storedLoginId, storedCustomerId) !== 'authenticated';
}
